import { Dropdown, GoogleMapModal, MultiselectDropdown, RadioButton, Stepper, TextInput } from '@components/common';
import { useStepperStore } from '@store/stepperStore';
import { useFormik } from 'formik';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { defaultAddSiteLeadFormInitialValues, substratesOptions } from '.';
import {
	brandUsedList,
	glueUsedRadioTypeList,
	QR_KEYS,
	SUPERVISOR_ALLOWED_CLASS,
	typeOfContractor,
} from '@config/constant';
import { Modal } from '@components/common/modal/modal';
import { useGoogleMapStore } from '@store/googleMapStore';
import { useValidation } from '@hooks/validations';
import { useFetchGrades } from '@services/hooks/competition-conversion/useFetchGrades';
import { Wrapper } from '@components/common/Wrapper/wrapper';
import { useCompetitionConversionUsageQuery } from '@services/hooks/competition-conversion/useCompetitionConversionUsageQuery';
import { useSiteLeadStore } from '@store/siteLeadStore';
import { useSetMainHeader } from '@hooks/useSetMainHeader';
import { useSiteLeadQuery, useUpdatedNewSiteLead } from '@services/hooks/site-leads';
import { useGetRxDb } from '@hooks/getRxdbData';
import { AllEndUserObject } from '@components/members/components/allEndUserList';
import { DropdownOptions } from '@components/common/formControl/dropdown';
import { isNullOrEmpty, parseMultiselectOptions } from '@components/delaerVisit/components/addEditDealerVisit';
import { SixMonthsConfirmationModal } from '@components/contractor360/components';
import { useEndUserStore } from '@store/enduser';
import { useEndUserProfileStore } from '@store/endUserProfileStore';
import { useCheckEndUserProfileFields } from '@services/hooks/endUserProfile';
import { ProductRecommendationModal } from '@components/contractor360/components/productReommendation/productRecommendation';
import { useHandleProductRecommendation } from '@components/contractor360/components/productReommendation';
import { ProfileData } from '@services/hooks/profile';
import { useContractorDetailQuery } from '@services/hooks/contractor360/useContractorDetailQuery';
import {
	RoleType,
	processSecondaryCompetitionReason,
	parseSecondaryCompetitionReasonFromProposedField,
} from '@helper/utils';
import { overlayRemoveFadeAction } from '@components/common/sort';
import { ContractorAidModals } from '@components/contractor360KAE/contractorAidModals';
import { useUserRoleType } from '@hooks/useUserRoleType';
import { VisitCompleteQr } from '../visitCompleteQr/visitCompleteQr';
import { useQrVerificationStore } from '@store/qrVerificationStore';
import { ToastType, useTosterStore } from '@store/toster';
import { BuilderDetailsSection } from '@components/common/builderDetails/builderDetails';
import { useComponentLevelAccessCheck } from '../../../../authGuard/hooks/useComponentLevelAccessCheck';
import { useShowPauseReminderOnVisitFormEdit } from '@components/siteLeads/siteVisitRecording/useShowPauseReminderOnVisitFormEdit';
import { useSiteVisitRecordingStore } from '@store/siteVisitRecordingStore';

export const AddNewSite = (): ReactElement => {
	useShowPauseReminderOnVisitFormEdit();
	const [addFields, setAddFields] = useState({});
	useSetMainHeader('Add New Site');
	const { setCurrentStep } = useStepperStore();
	const { currentAddress } = useGoogleMapStore();
	const { AddSiteLeadValidationSchema } = useValidation();
	const [locationModal, setLocationModal] = useState<boolean>(false);
	const { data: gradeList, error: gradeListError, isPending: isGradeListPending } = useFetchGrades();
	const { sfid, addNewSiteLead, setKeyBaseValue, siteDetails, siteVisitId } = useSiteLeadStore();
	const { resetFor } = useQrVerificationStore();
	const {
		data: competitionList,
		error: competitionListError,
		isPending: iscompetitionListPending,
	} = useCompetitionConversionUsageQuery();
	const { data: siteData, isFetching: isSiteDataFetching, error: siteDataError } = useSiteLeadQuery();
	const { result: allEndUser } = useGetRxDb<AllEndUserObject>('all');
	const { mutate: updateSiteLead } = useUpdatedNewSiteLead();
	const [selectedContractor, setSelectedContractor] = useState<AllEndUserObject | undefined>(undefined);
	const [isOpen, setModal] = useState<boolean>(false);
	const { setFormRevalidate, setFormId } = useEndUserStore();
	const [verifyQrModal, setVerifyQrModal] = useState<boolean>(false);
	const { setToaster } = useTosterStore();
	const isSiteQrVerified = useQrVerificationStore((s) => s.isVerified(QR_KEYS.SITE));
	const {
		basicDetailsRevalidate,
		moreDetailsRevalidate,
		isAIDOpen,
		isNoOfCarpenterOpen,
		setContractorAidModals,
		resetContractorAidModals,
		setKeyBaseValue: setEndUserKeyBaseValue,
	} = useEndUserProfileStore();
	const [totalSteps, setTotalSetps] = useState<number>(3);
	const { result: profile } = useGetRxDb<ProfileData>('profile');
	const role = profile[0]?.usertype_c;
	const userRoleType = useUserRoleType();
	const contractorQuery = useContractorDetailQuery(selectedContractor?.id ?? 0, '/all', {
		enabled: !!selectedContractor?.id,
	});
	const {
		data: endUserDetails,
		error: getEndUserDetailsError,
		isFetching: isGetEndUserDetailsFetching,
		refetch: endUserDetailsRefetch,
	} = contractorQuery;
	const builderPermissionCheck = useComponentLevelAccessCheck('builder-details-section');
	useCheckEndUserProfileFields();
	const {
		handleProductRecommendation,
		isProductRecommendationOpen,
		productRecommendationData,
		closeProductRecommendation,
	} = useHandleProductRecommendation({
		membershipno: selectedContractor?.membershipno,
		contractorclass: selectedContractor?.contractorclass,
		banking_mtd_kgs: selectedContractor?.banking_mtd_kgs,
	});

	useEffect(() => {
		if (selectedContractor?.id) {
			setFormId(selectedContractor.id);
			setFormRevalidate(true);
		}
	}, [setFormId, setFormRevalidate, selectedContractor]);

	useEffect(() => {
		resetFor('site-qr');
	}, [resetFor]);

	const formik = useFormik({
		initialValues: defaultAddSiteLeadFormInitialValues,
		onSubmit: () => {},
		validationSchema: AddSiteLeadValidationSchema,
		validate: (values) => {
			const errors: Record<string, string> = {};

			// Builder details are required when competition reason includes "builder" (e.g. "Builder Chooses Adhesive")
			// and user doesn't have builder permission (builder section is visible)
			const competitionReasonIncludesBuilder = String(values.competitionReason ?? '')
				.toLowerCase()
				.trim()
				.includes('builder');
			const shouldValidateBuilder =
				values.glueUsedAtSite?.toLowerCase() === 'competition' &&
				competitionReasonIncludesBuilder &&
				!builderPermissionCheck;

			if (shouldValidateBuilder) {
				if (!values.builder_name?.trim()) {
					errors.builder_name = 'Builder Name is required';
				}

				if (!values.builder_phone_number?.trim()) {
					errors.builder_phone_number = 'Builder Contact Number is required';
				} else if (!/^\d{10}$/.test(values.builder_phone_number.trim())) {
					errors.builder_phone_number = 'Enter a valid 10-digit contact number';
				}
			} else {
				// Explicitly clear builder errors when condition is not met
				// This ensures errors are removed when switching away from builder-related competition reason
				delete errors.builder_name;
				delete errors.builder_phone_number;
				delete errors.builder_office_address;
			}

			// Clear builder validation errors when user has permission
			if (builderPermissionCheck) {
				delete errors.builder_name;
				delete errors.builder_phone_number;
				delete errors.builder_office_address;
			}

			return errors;
		},
	});

	// When user has builder permission, clear builder errors. Do NOT run full validateForm()
	// here—it would set contractorName (and other) errors when values are still empty on mount.
	useEffect(() => {
		if (builderPermissionCheck) {
			formik.setFieldError('builder_name', undefined);
			formik.setFieldError('builder_phone_number', undefined);
			formik.setFieldError('builder_office_address', undefined);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [builderPermissionCheck]);

	// Revalidate form when competition reason or glue used at site changes
	// This ensures validation runs when a builder-related competition reason is selected or deselected
	useEffect(() => {
		const competitionReasonIncludesBuilder = String(formik.values.competitionReason ?? '')
			.toLowerCase()
			.trim()
			.includes('builder');
		const shouldValidateBuilder =
			formik.values.glueUsedAtSite?.toLowerCase() === 'competition' &&
			competitionReasonIncludesBuilder &&
			!builderPermissionCheck;

		// If builder validation is not needed, explicitly clear builder errors
		if (!shouldValidateBuilder) {
			if (formik.errors.builder_name) {
				formik.setFieldError('builder_name', undefined);
			}
			if (formik.errors.builder_phone_number) {
				formik.setFieldError('builder_phone_number', undefined);
			}
			if (formik.errors.builder_office_address) {
				formik.setFieldError('builder_office_address', undefined);
			}
		}

		// Only validate builder fields specifically to avoid triggering validation for other untouched fields
		// This prevents showing errors for contractorName when it hasn't been touched
		if (shouldValidateBuilder) {
			// Validate only builder fields when they become required
			formik.validateField('builder_name').catch(() => {});
			formik.validateField('builder_phone_number').catch(() => {});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formik.values.competitionReason, formik.values.glueUsedAtSite, builderPermissionCheck]);
	const [maxLimit, setMaxLimit] = useState<number>(3);

	const updateSiteLeadData = useCallback((): void => {
		// Process secondaryCompetitionReason: replace "Other" with custom text value
		const processedSecondaryCompetitionReason = processSecondaryCompetitionReason(
			formik.values.secondaryCompetitionReason,
			formik.values.secondaryCompetitionReasonOther
		);

		const payload = {
			name: formik.values.siteName,
			site_location_c: formik.values.siteLocation,
			end_user_c: siteDetails?.sfid,
			glue_used_at_the_site: formik.values.glueUsedAtSite,
			grade_glue_used_at_the_site: formik.values.gradeUsed,
			reason_of_competition: formik.values.glueUsedAtSite === 'Competition' ? formik.values.competitionReason : null,
			type_of_contract:
				typeOfContractor.find((contractor) => contractor.value === formik.values.contractType)?.name ?? '',
			brand_of_glue_used_at_site: formik.values.glueUsedAtSite === 'Competition' ? formik.values.brandUsed : '',
			market: 'BV',
			contract_phone: formik.values?.contractorName,
			contract_name: formik.values?.contractorNumber,
			roff_substrate_c: isNullOrEmpty(formik.values.substratesCombinations),
			proposed_field_competition_sites: isNullOrEmpty(processedSecondaryCompetitionReason),

			builder_name: formik.values.builder_name,
			builder_phone_number: formik.values.builder_phone_number,
			builder_office_address: formik.values.builder_office_address,
		};
		updateSiteLead?.(payload);
	}, [formik.values, siteDetails?.sfid, updateSiteLead]);

	const handleSiteEndUserProfile = (callback: () => void): void => {
		if (basicDetailsRevalidate || moreDetailsRevalidate) {
			setModal(true);
			return;
		}

		if (endUserDetails?.id) {
			const shouldShowModal = setContractorAidModals({
				is_aid_c: endUserDetails.is_aid_c,
				no_of_carpenters: endUserDetails.no_of_carpenters,
				glue_consumption_per_month: endUserDetails.glue_consumption_per_month,
				showCarpenterModal: userRoleType === RoleType.KAE,
			});
			if (shouldShowModal) return;
		}

		callback();
	};

	const handleNext = useCallback((): void => {
		const {
			gradeUsed,
			contractType,
			competitionReason,
			glueUsedAtSite,
			siteName,
			siteLocation,
			brandUsed,
			substratesCombinations,
			secondaryCompetitionReason,
			secondaryCompetitionReasonOther,
			builder_name,
			builder_phone_number,
			builder_office_address,
		} = formik.values;
		const contractorTypeValue = typeOfContractor.find((contractor) => contractor.value === contractType)?.name ?? '';
		const reasonForCompetition = glueUsedAtSite?.toLowerCase() === 'competition' ? competitionReason : '';
		const processedSecondaryCompetitionReason = processSecondaryCompetitionReason(
			secondaryCompetitionReason,
			secondaryCompetitionReasonOther
		);
		const keyBaseValues = {
			grade: gradeUsed,
			brandUsed,
			type_of_contract: contractorTypeValue,
			reason: reasonForCompetition,
			glue: glueUsedAtSite,
			siteName,
			siteLocation,
			substrates: substratesCombinations,
			proposed_field_competition_sites: isNullOrEmpty(processedSecondaryCompetitionReason),
			builder_name,
			builder_phone_number,
			builder_office_address,
		};
		Object.entries(keyBaseValues).forEach(([key, value]) => {
			setKeyBaseValue(key, value);
		});

		if (!siteData?.glue_used_at_the_site && sfid) {
			updateSiteLeadData();
		}

		const stepNavigate =
			siteData?.visits?.find((visit) => visit.id === siteVisitId)?.site_visit_status?.toLowerCase() === 'draft' ? 2 : 1;
		setCurrentStep(stepNavigate);

		if (addNewSiteLead) {
			setKeyBaseValue('siteDetails', addFields);
		}
	}, [
		sfid,
		formik.values,
		siteData,
		setCurrentStep,
		addNewSiteLead,
		setKeyBaseValue,
		updateSiteLeadData,
		siteVisitId,
		addFields,
	]);

	const handleLocationSubmit = useCallback((): void => {
		formik.setFieldValue('siteLocation', currentAddress);
		setLocationModal(false);
	}, [currentAddress, formik]);

	const handleOpenSiteLocationModal = (): void => {
		setLocationModal((prevState) => {
			if (!prevState) {
				const recordingStore = useSiteVisitRecordingStore.getState();
				if (recordingStore.phase === 'paused') {
					recordingStore.notifyPausedFormEdit();
					return prevState;
				}
			}
			return !prevState;
		});
	};

	const handleCloseSiteLocationModal = (): void => {
		setLocationModal(false);
	};

	// Only disable contractor dropdown when a valid option is selected (not during typing/search)
	const isContractorNameDisabled = useMemo(() => {
		const contractorNames = allEndUser.map((user) => user.name);
		const isValidSelection = contractorNames.includes(formik.values.contractorName ?? '');
		return isValidSelection && !addNewSiteLead;
	}, [allEndUser, formik.values.contractorName, addNewSiteLead]);

	const gradeOptions = useMemo(() => {
		if (!gradeList?.length) return [];
		return gradeList?.map((grade, index) => {
			return {
				id: index,
				value: grade.name,
			};
		});
	}, [gradeList]);

	const competitionOptions = useMemo(() => {
		if (!competitionList?.length) return [];
		return competitionList.map((competition, index) => {
			return {
				id: index,
				value: competition.name,
			};
		});
	}, [competitionList]);

	const secondaryCompetitionOptions = useMemo(() => {
		const options = competitionOptions.length > 0 ? [...competitionOptions] : [];
		// Add "Other" option at the end
		options.push({
			id: competitionOptions.length,
			value: 'Other',
		});
		return options;
	}, [competitionOptions]);

	const handleMultiSelectChange = (e: DropdownOptions): void => {
		const hasNA = formik.values.substratesCombinations.some((v) => v.value === 'NA');
		const isNASelected = e.value === 'NA';

		setMaxLimit(isNASelected && !hasNA ? 1 : 3);
		if (isNASelected) {
			formik.setFieldValue('substratesCombinations', hasNA ? [] : [{ id: 7, value: 'NA' }]);
		}
	};

	useEffect(() => {
		const contractorClass = siteDetails?.contractorclass || selectedContractor?.contractorclass;

		if (role === RoleType.KAE && SUPERVISOR_ALLOWED_CLASS.includes(contractorClass || '')) {
			setTotalSetps(4);
		} else {
			setTotalSetps(3);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [siteDetails, role, selectedContractor]);
	useEffect(() => {
		if (siteDetails) {
			formik.setValues(
				{
					...formik.values,
					contractorName: siteDetails?.name ?? '',
					contractorNumber: siteDetails?.number ?? '',
				},
				true
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [siteDetails]);

	// Clear "Other" text field when "Other" option is deselected
	useEffect(() => {
		const hasOther = formik.values.secondaryCompetitionReason.some((item) => item.value === 'Other');
		if (!hasOther && formik.values.secondaryCompetitionReasonOther) {
			formik.setFieldValue('secondaryCompetitionReasonOther', '');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formik.values.secondaryCompetitionReason]);

	useEffect(() => {
		if (siteData) {
			// Parse proposed_field_competition_sites to auto-fill secondaryCompetitionReason
			const parsedSecondaryReason = parseSecondaryCompetitionReasonFromProposedField(
				siteData.proposed_field_competition_sites,
				secondaryCompetitionOptions
			);

			// Use builder values from backend when present (edit mode)
			const hasBuilderData = !!(
				siteData.builder_name ||
				siteData.builder_phone_number ||
				siteData.builder_office_address
			);

			formik.setValues(
				{
					siteName: siteData.name,
					contractorName: siteData.contract_name ?? siteData.lead_contractor_name,
					contractorNumber: siteData.contract_phone ?? siteData.lead_contractor_phone,
					contractType: typeOfContractor.find((item) => item.name === siteData.type_of_contract)?.value,
					gradeUsed: siteData.grade_glue_used_at_the_site,
					brandUsed: siteData.brand_of_glue_used_at_site,
					siteLocation: siteData.site_location_c,
					competitionReason: siteData.reason_of_competition,
					glueUsedAtSite: siteData.glue_used_at_the_site,
					secondaryCompetitionReason: parsedSecondaryReason.options,
					secondaryCompetitionReasonOther: parsedSecondaryReason.otherText ?? '',
					substratesCombinations: parseMultiselectOptions(siteData.roff_substrate_c, substratesOptions),
					builder_name: hasBuilderData ? siteData.builder_name ?? '' : formik.values.builder_name,
					builder_phone_number: hasBuilderData
						? siteData.builder_phone_number ?? ''
						: formik.values.builder_phone_number,
					builder_office_address: hasBuilderData
						? siteData.builder_office_address ?? ''
						: formik.values.builder_office_address,
				},
				true
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [siteData]);

	const handleVerifyModalClose = useCallback((): void => {
		setVerifyQrModal(false);
		overlayRemoveFadeAction(() => {});
	}, []);

	const handleVerifyModalOpen = useCallback((): void => {
		if (isSiteQrVerified) {
			setToaster(true, ToastType.success, 'Visit is already verified.');
			return;
		}
		const recordingStore = useSiteVisitRecordingStore.getState();
		if (recordingStore.phase === 'paused') {
			recordingStore.notifyPausedFormEdit();
			return;
		}
		setVerifyQrModal(true);
	}, [isSiteQrVerified, setToaster]);

	const handleNextValidate = (): void => {
		handleSiteEndUserProfile(() => {
			handleProductRecommendation(() => {
				handleNext();
			});
		});
	};

	const closeAIDModal = useCallback(() => {
		overlayRemoveFadeAction(() => setEndUserKeyBaseValue('isAIDOpen', false));
	}, [setEndUserKeyBaseValue]);

	const closeCarpenterModal = useCallback(() => {
		overlayRemoveFadeAction(() => setEndUserKeyBaseValue('isNoOfCarpenterOpen', false));
	}, [setEndUserKeyBaseValue]);

	useEffect(() => {
		resetContractorAidModals();
	}, [resetContractorAidModals]);

	const hasBuilderDataFromBackend = !!(siteData?.builder_name || siteData?.builder_phone_number);

	return (
		<section className="new-site-section">
			<Wrapper
				isLoaderVisible={isGradeListPending || iscompetitionListPending || isSiteDataFetching}
				isError={gradeListError || competitionListError || siteDataError}
			>
				<div className="container">
					<form onSubmit={formik.handleSubmit}>
						<ul className="new-site-wrapper row">
							<li className="form-control d-col d-col-2">
								<TextInput
									id="siteName"
									name="siteName"
									type="text"
									onBlur={formik.handleBlur}
									value={formik.values.siteName ?? ''}
									setFieldValue={formik.setFieldValue}
									label="Site Name"
									error={formik.touched.siteName && formik.errors.siteName ? formik.errors.siteName : null}
									disabled={!!(sfid && siteData?.name)}
									isAutoFocus
									required
								/>
							</li>

							<li className="form-control d-col d-col-2  location">
								<TextInput
									id="siteLocation"
									name="siteLocation"
									onBlur={formik.handleBlur}
									type="text"
									value={formik.values.siteLocation ?? ''}
									setFieldValue={formik.setFieldValue}
									label="Site Location"
									error={formik.touched.siteLocation && formik.errors.siteLocation ? formik.errors.siteLocation : null}
									disabled={!!(sfid && siteData?.site_location_c)}
									required
								/>
								<button
									type="button"
									aria-label="map link"
									className="icon-location map"
									onClick={handleOpenSiteLocationModal}
									disabled={!!(sfid && siteData?.site_location_c)}
								/>
							</li>

							<li className="d-col d-col-2  form-radio">
								<RadioButton
									name="glueUsedAtSite"
									title="Glue Used at Site"
									checked={formik.values.glueUsedAtSite ?? ''}
									radioOptions={glueUsedRadioTypeList}
									onChange={formik.handleChange}
									isDisabled={!!(sfid && siteData?.glue_used_at_the_site)}
									required
								/>
							</li>

							{formik.values.glueUsedAtSite === 'Competition' && (
								<li className="d-col d-col-2 ">
									<Dropdown
										id="brandUsed"
										name="brandUsed"
										label="Brand of Glue used at Site"
										title={formik.values.brandUsed ?? ''}
										options={brandUsedList}
										setFieldValue={formik.setFieldValue}
										disabled={!!(sfid && siteData?.brand_of_glue_used_at_site)}
										onBlur={formik.handleBlur}
										onChange={(e) => {
											formik.setFieldValue('brandUsed', e.value);
											const selectedBrand = brandUsedList.find((item) => item.value === e.value);
											formik.setFieldValue('gradeUsed', selectedBrand?.grade ?? '');
										}}
										required
									/>
								</li>
							)}

							<li className="d-col d-col-2 ">
								<Dropdown
									id="gradeUsed"
									name="gradeUsed"
									label="Grade of Glue used at Site"
									onBlur={formik.handleBlur}
									title={formik.values.gradeUsed ?? ''}
									error={formik.touched.gradeUsed && formik.errors.gradeUsed ? formik.errors.gradeUsed : null}
									options={gradeOptions}
									setFieldValue={formik.setFieldValue}
									disabled={
										!!(sfid && siteData?.grade_glue_used_at_the_site) || formik.values.glueUsedAtSite === 'Competition'
									}
									required
								/>
							</li>

							<li className="d-col d-col-2 ">
								<Dropdown
									id="contractType"
									name="contractType"
									label="Type of Contract"
									onBlur={formik.handleBlur}
									title={formik.values.contractType ?? ''}
									error={formik.touched.contractType && formik.errors.contractType ? formik.errors.contractType : null}
									options={typeOfContractor}
									setFieldValue={formik.setFieldValue}
									disabled={!!(sfid && siteData?.type_of_contract)}
									required
								/>
							</li>

							<li className="d-col d-col-2 single-line-multiselect">
								<MultiselectDropdown
									id="substratesCombinations"
									name="substratesCombinations"
									label="Are any of the below substrates combinations being used at site?"
									className="input-border single-lable-multi-dropdown"
									onBlur={formik.handleBlur}
									values={formik.values.substratesCombinations}
									options={substratesOptions}
									setFieldValue={formik.setFieldValue}
									maxLimit={maxLimit}
									onChange={handleMultiSelectChange}
									required
									disabled={!!(sfid && siteData?.roff_substrate_c)}
									error={
										formik.touched.substratesCombinations && formik.errors.substratesCombinations
											? formik.errors.substratesCombinations.toString()
											: null
									}
								/>
							</li>

							{formik.values.glueUsedAtSite?.toLowerCase() === 'competition' && (
								<li className="d-col d-col-2 ">
									<Dropdown
										id="competitionReason"
										name="competitionReason"
										label="Primary Reason For Competition"
										onBlur={formik.handleBlur}
										title={formik.values.competitionReason ?? ''}
										error={
											formik.touched.competitionReason && formik.errors.competitionReason
												? formik.errors.competitionReason
												: null
										}
										options={competitionOptions}
										setFieldValue={formik.setFieldValue}
										disabled={!!(sfid && siteData?.reason_of_competition)}
										required
									/>
								</li>
							)}

							{formik.values.glueUsedAtSite?.toLowerCase() === 'competition' &&
								(builderPermissionCheck ||
									hasBuilderDataFromBackend ||
									String(formik.values.competitionReason ?? '')
										.toLowerCase()
										.trim()
										.includes('builder')) && (
									<BuilderDetailsSection
										formik={formik}
										isVisible={
											String(formik.values.competitionReason ?? '')
												.toLowerCase()
												.trim()
												.includes('builder') ||
											!!(siteData?.builder_name || siteData?.builder_phone_number || siteData?.builder_office_address)
										}
										disabled={!!sfid}
									/>
								)}
							{formik.values.glueUsedAtSite?.toLowerCase() === 'competition' && (
								<li className="d-col d-col-2 ">
									<MultiselectDropdown
										id="secondaryCompetitionReason"
										name="secondaryCompetitionReason"
										label="Secondary Reason For Competition"
										onBlur={formik.handleBlur}
										values={formik.values.secondaryCompetitionReason}
										error={
											formik.touched.secondaryCompetitionReason && formik.errors.secondaryCompetitionReason
												? formik.errors.secondaryCompetitionReason.toString()
												: null
										}
										options={secondaryCompetitionOptions}
										setFieldValue={formik.setFieldValue}
										disabled={!!(sfid && siteData?.proposed_field_competition_sites)}
									/>
								</li>
							)}
							{formik.values.glueUsedAtSite?.toLowerCase() === 'competition' &&
								formik.values.secondaryCompetitionReason.some((item) => item.value === 'Other') && (
									<li className="form-control d-col d-col-2">
										<TextInput
											id="secondaryCompetitionReasonOther"
											name="secondaryCompetitionReasonOther"
											type="text"
											onBlur={formik.handleBlur}
											value={formik.values.secondaryCompetitionReasonOther ?? ''}
											setFieldValue={formik.setFieldValue}
											label="Please specify other reason"
											error={
												formik.touched.secondaryCompetitionReasonOther && formik.errors.secondaryCompetitionReasonOther
													? formik.errors.secondaryCompetitionReasonOther
													: null
											}
											disabled={!!(sfid && siteData?.proposed_field_competition_sites)}
											required
										/>
									</li>
								)}

							<li className="d-col d-col-2 ">
								<Dropdown
									id="contractorName"
									name="contractorName"
									label="Contractor Name"
									onBlur={(e) => {
										if (!e.target.value) {
											formik.setFieldValue('contractorNumber', '');
										}
										formik.handleBlur(e);
									}}
									title={formik.values.contractorName ?? ''}
									error={
										formik.touched.contractorName && formik.errors.contractorName ? formik.errors.contractorName : null
									}
									options={allEndUser.map(
										(user): DropdownOptions => ({
											id: user.sfid,
											name: user.sfid,
											value: user.name,
										})
									)}
									setFieldValue={formik.setFieldValue}
									disabled={isContractorNameDisabled}
									onChange={(e) => {
										resetFor('site-qr');
										const user = allEndUser.find((user) => user.sfid === e.id);
										setSelectedContractor(user);
										// Use setValues to update both fields at once, ensuring single validation run
										formik.setValues(
											{
												...formik.values,
												contractorName: user?.name ?? '',
												contractorNumber: user?.phone ?? '',
											},
											true
										);
										setAddFields({
											name: user?.name,
											number: user?.phone,
											sfid: user?.sfid,
											membershipno: user?.membershipno,
											contractorclass: user?.contractorclass,
										});
									}}
									required
								/>
							</li>

							<li className="d-col d-col-2 ">
								<Dropdown
									id="contractorNumber"
									name="contractorNumber"
									label="Contractor Number"
									onBlur={formik.handleBlur}
									title={formik.values.contractorNumber ?? ''}
									error={
										formik.touched.contractorNumber && formik.errors.contractorNumber
											? formik.errors.contractorNumber
											: null
									}
									options={allEndUser.map(
										(user): DropdownOptions => ({
											id: user.sfid,
											name: user.sfid,
											value: user.phone,
										})
									)}
									setFieldValue={formik.setFieldValue}
									disabled
									required
								/>
							</li>
						</ul>
						<div className="verify-site-btn">
							<button
								type="button"
								id="verify-site-button"
								aria-label="verify-site-button"
								className="btn btn-secondary btn-small verify-site-button"
								value="verify-site-button"
								onClick={handleVerifyModalOpen}
							>
								Verify Site Visit
							</button>
						</div>
					</form>

					<Modal
						modalId="googleMapModal"
						className="map-popup dialog-sm md-large pt-4"
						modalOpen={locationModal}
						onModalClose={handleCloseSiteLocationModal}
					>
						<GoogleMapModal handleConfirm={handleLocationSubmit} handleClose={handleCloseSiteLocationModal} />
					</Modal>
					<Stepper nextHandler={handleNextValidate} isNext={!formik.isValid || !formik.dirty} totalSteps={totalSteps} />
				</div>
			</Wrapper>
			<Modal
				modalOpen={isOpen}
				modalId={'closeLeads'}
				onModalClose={() => {
					setModal(false);
				}}
				className="dialog-sm close-site-popup dialog-conform"
			>
				<SixMonthsConfirmationModal
					message={'Kindly review and confirm the highlighted enrollment data.'}
					modalClose={() => setModal(false)}
					data={selectedContractor}
				/>
			</Modal>
			{isProductRecommendationOpen.modalOpen && (
				<Modal
					modalTitle={'Product Recommendation'}
					modalOpen={isProductRecommendationOpen.modalOpen}
					modalId={'productRecommendation'}
					onModalClose={closeProductRecommendation}
					className="dialog-xxl dialog-bottom close-site-popup dialog-conform"
				>
					<ProductRecommendationModal
						modalClose={closeProductRecommendation}
						onSubmit={() => {
							closeProductRecommendation();
							handleNext();
						}}
						membershipNo={selectedContractor?.membershipno}
						productRecommendationData={productRecommendationData}
						isUpdate={isProductRecommendationOpen.isUpdate}
					/>
				</Modal>
			)}
			{endUserDetails?.id && !isGetEndUserDetailsFetching && !getEndUserDetailsError && (
				<ContractorAidModals
					showCarpenterModal={userRoleType === RoleType.KAE}
					userId={endUserDetails.id}
					isAIDOpen={isAIDOpen}
					isNoOfCarpenterOpen={isNoOfCarpenterOpen}
					closeAIDModal={closeAIDModal}
					closeCarpenterModal={closeCarpenterModal}
					refetch={endUserDetailsRefetch ?? ((): void => {})}
				/>
			)}
			{verifyQrModal && (
				<Modal
					modalId="verifyQrModal"
					className="dialog-lg dialog-sm"
					modalOpen={verifyQrModal}
					isEscAllow={false}
					onModalClose={handleVerifyModalClose}
				>
					<VisitCompleteQr siteDetails={siteDetails ?? addFields} verificationKey={QR_KEYS.SITE} />
				</Modal>
			)}
		</section>
	);
};
